/**
 * main.tsx — v.1-D §7.21.
 *
 * Provider tree mounted at the document root. The order is load-bearing:
 *   AppErrorBoundary
 *     └─ PayloadAuthProvider                  (Payload credential session)
 *         └─ ConvexAuthProvider               (Convex Auth OAuth — D-012)
 *             └─ ConvexProvider               (Convex reactive client)
 *                 ├─ RouterProvider           (the route tree from src/routes)
 *                 └─ Toaster                  (Sonner — sibling, inside ConvexProvider)
 *
 * AppErrorBoundary is the outermost ring so a provider-construction error
 * surfaces as the full-screen 500 card rather than a blank page. Toaster is
 * a sibling of RouterProvider inside ConvexProvider so toasts can subscribe
 * to Convex queries (e.g. an action-failed toast referencing live data).
 *
 * Two window-level error listeners forward uncaught failures into the
 * structured logger so even non-React errors land in the observability pipe.
 */
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ConvexProvider } from 'convex/react';
import { ConvexAuthProvider } from '@convex-dev/auth/react';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { convex } from './lib/convex';
import { PayloadAuthProvider } from './lib/payloadAuth';
import { AppErrorBoundary } from './layouts/AppErrorBoundary';
import { router } from './routes';
import { logger } from './lib/logger';
import './styles/global.css';
import './i18n';

window.addEventListener('unhandledrejection', (event) => {
  logger.error('unhandled_promise_rejection', {
    reason:
      event.reason instanceof Error
        ? event.reason.message
        : String(event.reason),
  });
});

window.addEventListener('error', (event) => {
  logger.error('window_error', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  });
});

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in document');
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <PayloadAuthProvider>
        <ConvexAuthProvider client={convex}>
          <ConvexProvider client={convex}>
            <RouterProvider router={router} />
            <Toaster position="bottom-right" richColors closeButton />
          </ConvexProvider>
        </ConvexAuthProvider>
      </PayloadAuthProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
